import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation
} from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { StateService } from '@uirouter/core';
import { combineLatest, ReplaySubject } from 'rxjs';
import { takeWhile, tap } from 'rxjs/operators';
import Tabulator from 'tabulator-tables';
import { UnsavedChangesService } from '../../../../shared/modal-dialog';
import { LoggingService } from '../../../../shared/services/logging/logging.service';
import { ExpenseStatus } from '../../../expense-enums';
import { IExpenseLine } from '../../../IExpenseLine';
import { IExpenseReceipt } from '../../../IExpenseReceipt';
import { IExpenseSummary } from '../../../IExpenseSummary';

@Component({
    selector: 'ef-expense-receipt-list',
    templateUrl: './expense-receipt-list.component.html',
    styleUrls: ['./expense-receipt-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ExpenseReceiptListComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    private _expenseReceipts: Array<IExpenseReceipt>;
    @Input() public set expenseReceipts(value: Array<IExpenseReceipt>) {
        this.log.verbose({
            type: 'ExpenseSummaryListComponent:expenseReceipts:set',
            value
        });
        this._expenseReceipts = value;
        this.ReceiptsSubject.next(value);
    }
    public get expenseReceipts(): Array<IExpenseReceipt> {
        return this._expenseReceipts;
    }
    @Input() public set selectedDetail(value: IExpenseLine) {
        this.SelectedDetailSubject.next(value);
    }
    @Input() public set selectedExpense(value: IExpenseSummary) {
        this.log.verbose({
            type: 'ExpenseReceiptListComponent:selectedExpense',
            value
        });
        if (value) {
            this.selectedExpenseValue = value;
            this.ExpenseReportStatus = value.ExpenseReportStatus;
            this.showHideColumns();
        }
    }
    /** From parent: detail/receipt edit allowed (workflow, status, report date in entry window). */
    @Input() canEdit = false;
    @Input() canOpen = false;
    private selectedExpenseValue: IExpenseSummary;
    private ExpenseReportStatus?: ExpenseStatus;
    private ValidatingSubject = new ReplaySubject<boolean>(1);
    private _validating: boolean;
    @Input() set validating(value: boolean) {
        this._validating = value;
        this.ValidatingSubject.next(value);
    }
    get validating(): boolean {
        return this._validating;
    }
    private ApprovingSubject = new ReplaySubject<boolean>(1);
    private _approving: boolean;
    @Input() set approving(value: boolean) {
        this._approving = value;
        this.ApprovingSubject.next(value);
    }
    get approving(): boolean {
        return this._approving;
    }
    @Output() selectReceipt = new EventEmitter<IExpenseReceipt>();
    @Output() editReceipt = new EventEmitter<IExpenseReceipt>();
    @Output() deleteReceipt = new EventEmitter<IExpenseReceipt>();
    public selectedRowsCheckbox = { checked: false, indeterminate: false };
    private tabExpenseReceipts: Tabulator;
    private SelectedDetailSubject = new ReplaySubject<IExpenseLine>(1);
    private ReceiptsSubject = new ReplaySubject<Array<IExpenseReceipt>>(1);
    private active = true;
    constructor(
        private confirmservice: UnsavedChangesService,
        @Inject(LoggingService) private log: LoggingService,
        @Inject(StateService) private ss: StateService
    ) {}

    ngOnInit() {
        this.ReceiptsSubject.pipe(
            takeWhile(() => this.active),
            tap(receipts =>
                this.log.verbose({
                    type: 'ExpenseReceiptListComponent:ngOnInit',
                    receipts
                })
            )
        ).subscribe(receipts => {
            // this.createTabulator(receipts, []);
            if (this.tabExpenseReceipts) {
                this.tabExpenseReceipts.setData(receipts.map(x => ({ ...x })));
                setTimeout(() => {
                    this.tabExpenseReceipts.redraw(true);
                }, 500);
            }
        });

        combineLatest([this.SelectedDetailSubject, this.ReceiptsSubject])
            .pipe(takeWhile(() => this.active))
            .subscribe(([line, receipts]) => {
                if (!line || !this.tabExpenseReceipts) {
                    return;
                }
                const match = (receipts || []).find(
                    x => x.Sequenceid === line.SequenceId
                );
                // Wait for setData/DOM so selectRow + scrollToRow succeed
                setTimeout(() => {
                    if (!this.tabExpenseReceipts) {
                        return;
                    }
                    this.tabExpenseReceipts.deselectRow();
                    if (match?.Unique_Id == null) {
                        return;
                    }
                    try {
                        this.tabExpenseReceipts.selectRow(match.Unique_Id);
                        this.tabExpenseReceipts.scrollToRow(
                            match.Unique_Id,
                            'center',
                            false
                        );
                    } catch (err) {
                        this.log.warn({
                            type: 'ExpenseReceiptListComponent:selectReceiptRow',
                            err,
                            uniqueId: match.Unique_Id
                        });
                    }
                }, 0);
                this.log.verbose({
                    type: 'ExpenseReceiptListComponent:SelectedDetailSubject:ReceiptsSubject',
                    line,
                    receipts
                });
            });
    }
    ngOnDestroy() {
        this.active = false;
    }
    ngAfterViewInit(): void {
        this.createTabulator([], []);
        this.setupSubscriptions();
    }
    setupSubscriptions() {
        combineLatest([
            this.ReceiptsSubject,
            this.ApprovingSubject,
            this.ValidatingSubject
        ])
            .pipe(takeWhile(() => this.active))
            .subscribe(([receipts, approving, validating]) => {
                this.showHideColumns();
            });
    }
    private createTabulator(
        receipts: Array<IExpenseReceipt>,
        errors: Array<string>
    ) {
        const comp = this;
        this.log.verbose(
            'ExpenseReceiptListComponent::createTabulator:receipts, errors',
            receipts,
            errors
        );
        this.tabExpenseReceipts = new Tabulator('#tabExpenseReceipts', {
            data: receipts.map(e => ({ ...e })),
            maxHeight: '20vh',
            minHeight: '7vh',
            // paginationSize: 5,
            // paginationSizeSelector: [5, 10, 15, 20, 25],
            // pagination: 'local',
            columns: [
                // 2
                {
                    headerSort: false,
                    title: 'Expense Receipt #',
                    minWidth: 110,
                    field: '',
                    visible: true,
                    formatter: c => {
                        const data = c.getData() as IExpenseReceipt;
                        // const row = c.getRow();
                        // const e = c.getElement();
                        // const exp = row.getData() as IExpenseSummary;
                        // e.classList.add(ExpenseStatus[exp.ExpenseReportStatus]);
                        return `${data.ExpenseRptNbr}-${data.Sequenceid}`;
                    }
                },
                // 3
                {
                    minWidth: 101,
                    headerSort: false,
                    title: 'Payment Method',
                    field: 'PaymentMethod',
                    visible: true
                },
                // 4
                {
                    minWidth: 60,
                    headerSort: false,
                    title: 'Total',
                    field: 'ReceiptTotal',
                    visible: true,
                    formatter: 'money',
                    formatterParams: {
                        symbol: '$'
                    }
                },
                // 5
                {
                    minWidth: 60,
                    headerSort: false,
                    title: 'Tax',
                    field: 'ReceiptTax',
                    visible: true,
                    formatter: 'money',
                    formatterParams: {
                        symbol: '$'
                    }
                },
                // 6
                {
                    minWidth: 75,
                    headerSort: false,
                    title: 'Description',
                    field: 'ReceiptDescription',
                    visible: true
                },
                {
                    headerSort: false,
                    title: '',
                    formatter: c => {
                        const data = c.getData() as IExpenseReceipt;
                        const url = this.ss.href('receipt', {
                            receiptId: data.File_unique_id
                        });
                        if (data.File_unique_id) {
                            return `<a target="_blank" href="${url}">View</a>`;
                        }
                        return '';
                        // return `<a uiSref="receipt({receiptId: '${data.Unique_Id}'})" target="_blank">View</a>`;
                    }
                },
                {
                    minWidth: 50,
                    width: 50,
                    title: 'Delete',
                    field: 'delete',
                    visible: false,
                    formatter: c => {
                        this.log.verbose({
                            type: 'ExpenseReceiptListComponent:delete',
                            status: comp.ExpenseReportStatus
                        });

                        if (comp.ExpenseReportStatus === ExpenseStatus.Signed) {
                            return `<i class="pi pi-trash ef-button-danger ef-circle-button redText"></i>`;
                        }
                    },
                    cellClick: (e, c) => {
                        if (comp.ExpenseReportStatus === ExpenseStatus.Signed) {
                            this.onExpenseReceiptDelete.bind(this)(c.getData());
                        }
                    },
                    cellTap: (e, c) => {
                        if (comp.ExpenseReportStatus === ExpenseStatus.Signed) {
                            this.onExpenseReceiptDelete.bind(this)(c.getData());
                        }
                    },
                    headerSort: false
                },
                {
                    title: 'ExpenseRptNbr',
                    field: 'ExpenseRptNbr',
                    visible: false
                },
                {
                    title: 'Sequenceid',
                    field: 'Sequenceid',
                    visible: false
                },
                {
                    title: 'Unique_Id',
                    field: 'Unique_Id',
                    visible: false
                }
            ],
            index: 'Unique_Id',
            layout: 'fitColumns',
            rowDblClick: this.onReceiptClick.bind(this),
            rowDblTap: this.onReceiptClick.bind(this),
            selectable: 1,
            initialSort: [
                { column: 'ExpenseRptNbr', dir: 'asc' },
                { column: 'Sequenceid', dir: 'asc' }
            ]
        });
    }
    private onReceiptClick(e, row) {
        const data = row.getData();
        this.log.debug({
            type: 'ExpenseReceiptListComponent:onReceiptClick',
            data
        });
        if (!this.canOpen) {
            return;
        }
        this.editReceipt.emit(data);
    }
    private onExpenseReceiptDelete(receipt: IExpenseReceipt) {
        this.log.verbose({
            type: 'ExpenseReceiptListComponent:onExpenseReceiptDelete',
            summary: receipt
        });
        this.confirm(this.deleteReceipt, receipt);
    }
    confirm(emitter: EventEmitter<IExpenseReceipt>, receipt: IExpenseReceipt) {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        this.confirmservice
            .confirmPromise({
                dialogText: 'Are you sure you want to delete this receipt?'
            })
            .then(() => {
                emitter.emit(receipt);
            })
            .catch(() => {});
    }

    showHideColumns() {
        this.log.verbose({
            type: 'ExpenseReceiptListComponent:showHideColumns',
            validating: this.validating,
            approving: this.approving,
            tab: this.tabExpenseReceipts
        });
        if (this.tabExpenseReceipts) {
            if (this.validating || this.approving) {
                this.tabExpenseReceipts.hideColumn('delete');
            } else {
                this.tabExpenseReceipts.showColumn('delete');
            }
        }
    }
}
